import {
  useRouteBodyState,
  RouteBodyStateScope,
} from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { createMaterialTopTabBarProps } from "@-/dev.improve.haniverse.components.ui.floating-tab-bar";
import { resolveRouteParam } from "@-/com.npmjs.expo-router";
import { View } from "@-/com.npmjs.react-native";
import { BrowseMnemonicTabs } from "#browse-mnemonic-tabs";
import { RootContext } from "@-/dev.improve.haniverse.routes._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { usePrevalentPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.prevalence";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { toDiacriticPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import {
  GraphemeMorphemePanelsContext,
  MorphemePanelsContext,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { GraphemeMnemonicsMeaningScreen } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._screens.meaning";
import { GraphemeMnemonicsPronunciationScreen } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._screens.pronunciation";
import { MorphemeMnemonicsViewTabBar } from "#morpheme-mnemonics-view-tab-bar";
import { MorphemeMnemonicsTabHostContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels.tab-host-context";
import { useCallback, useContext, useEffect, useMemo, type ReactElement } from "react";
import { TabView } from "@-/com.npmjs.react-native-tab-view";
import type { GraphemePageTabs } from "#morpheme-mnemonics-view";

const pinyinScreenName = (toneNumbersPinyin: string) => `[pinyin]:${toneNumbersPinyin}`;

const graphemeMnemonicsTabList = [
  { segment: "meaning", title: "Meaning" },
  { segment: "[pinyin]", title: "Pronunciation" },
] as const;

export function MorphemeMnemonicsViewSuspense({
  initialPinyin,
  onFirstTabChange,
  pageTabs,
  rawGrapheme,
  readingNavigation,
}: {
  initialPinyin?: string;
  // Android: reports whether the inner pager is on its first (meaning) tab. The
  // parent panel uses this to enable its own outer pager swipe only there, so a
  // rightward back-swipe steps to the info view (the inner pager has nothing
  // to its left on the first tab, so the outer pager can safely own that drag).
  onFirstTabChange?: (isOnFirstTab: boolean) => void;
  pageTabs?: GraphemePageTabs;
  rawGrapheme: string;
  readingNavigation: "card" | "browse";
}) {
  const tabs = graphemeMnemonicsTabList;
  const [morphemeHrefObject, panelContext] = useContextSelectors(
    MorphemePanelsContext,
    (c) => c.morphemeHrefObject,
    (c) => c,
  );
  const [layout] = useContextSelectors(RootContext, (c) => c.windowDimensions);
  // The panel's own route names the reading to open (`MorphemePanelsContext`), not whichever route
  // is focused: a Learn card or an in-card exploration sits under the deck route, whose params
  // describe a different card.
  const rawPinyinParam = morphemeHrefObject.params.pinyin;
  const initialPinyinParam =
    initialPinyin ?? (rawPinyinParam !== undefined ? resolveRouteParam(rawPinyinParam) : "");
  const grapheme = rawGrapheme.toString();
  // The gate that mounted these panels already narrowed the readonly slice, which carries the
  // `pinyinList` the view-tab-bar needs. Read it from there rather than re-observing the (nullable)
  // slice query for the same grapheme.
  const [pinyinList, back] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data.pinyinList,
    (c) => c.readonlySlice.data.back,
  );
  const prevalentPinyinList = usePrevalentPinyin(grapheme, pinyinList);
  const { isPinyinMnemonicsEnabled } = useActorUser();
  // A Learn card's binder tab always offers a section per reading, mirroring the browse page's
  // native reading tabs.
  const isHostedInLearnCard = useContext(MorphemeMnemonicsTabHostContext) !== null;
  // A pronunciation review must reveal its exact reading and mnemonic even when the learner
  // hides pronunciation tabs while browsing. Other surfaces keep honoring that preference.
  const studyPinyinList = useMemo(
    () =>
      readingNavigation === "browse"
        ? pinyinList
        : isPinyinMnemonicsEnabled || isHostedInLearnCard
          ? prevalentPinyinList
          : prevalentPinyinList.filter((entry) => entry.toneNumbersPinyin === initialPinyin),
    [
      initialPinyin,
      isHostedInLearnCard,
      isPinyinMnemonicsEnabled,
      prevalentPinyinList,
      pinyinList,
      readingNavigation,
    ],
  );
  const [selectedReading, setSelectedReading] = useRouteBodyState("mnemonics-reading", () =>
    initialPinyinParam !== "" &&
    studyPinyinList.some((entry) => entry.toneNumbersPinyin === initialPinyinParam)
      ? pinyinScreenName(initialPinyinParam)
      : "meaning",
  );

  const { screens, tabButtonList } = useMemo(() => {
    const screenEntries: {
      key: string;
      name: string;
      render: () => ReactElement;
      title: string;
    }[] = [];
    const tabButtonEntries: {
      name: string;
      pinyin: string | undefined;
      title: string;
    }[] = [];
    let readingIndex = 0;

    for (const tab of tabs) {
      if (tab.segment === "[pinyin]") {
        for (const pinyin of studyPinyinList) {
          const toneNumbersPinyin = pinyin.toneNumbersPinyin;
          screenEntries.push({
            key: morphemeHrefObject.href + pinyinScreenName(toneNumbersPinyin),
            name: pinyinScreenName(toneNumbersPinyin),
            render: () => (
              <GraphemeMnemonicsPronunciationScreen
                rawGrapheme={rawGrapheme}
                rawPinyin={toneNumbersPinyin}
              />
            ),
            title: toDiacriticPinyin(toneNumbersPinyin),
          });
          tabButtonEntries.push({
            name: `reading${readingIndex}`,
            pinyin: toneNumbersPinyin,
            title: toDiacriticPinyin(toneNumbersPinyin),
          });
          readingIndex += 1;
        }
        continue;
      }
      screenEntries.push({
        key: morphemeHrefObject.href + tab.segment,
        name: tab.segment,
        render: () => <GraphemeMnemonicsMeaningScreen />,
        title: readingNavigation === "browse" ? (back ?? tab.title) : tab.title,
      });
      tabButtonEntries.push({
        name: "meaning",
        pinyin: undefined,
        title: readingNavigation === "browse" ? (back ?? tab.title) : tab.title,
      });
    }
    if (pageTabs !== undefined) {
      screenEntries.push({
        key: morphemeHrefObject.href + ":details",
        name: "info",
        render: pageTabs.details,
        title: "Details",
      });
      tabButtonEntries.push({ name: "info", pinyin: undefined, title: "Details" });
    }
    return { screens: screenEntries, tabButtonList: tabButtonEntries };
  }, [back, morphemeHrefObject, rawGrapheme, studyPinyinList, tabs, readingNavigation, pageTabs]);

  // Persist the canonical tone-number reading key, not its rank in a refreshed prevalence list.
  const activeIndex = Math.max(
    0,
    screens.findIndex(
      (entry) => entry.name === (pageTabs?.isDetailsSelected ? "info" : selectedReading),
    ),
  );
  const setActiveIndex = useCallback(
    (next: number) => {
      const name = screens[next]?.name ?? "meaning";
      if (pageTabs !== undefined && !pageTabs.onDetailsChange(name === "info")) {
        return;
      }
      if (name !== "info") {
        setSelectedReading(name);
      }
    },
    [screens, setSelectedReading, pageTabs],
  );

  // A card-actions menu outside the readings (Details) asks to edit the flashcard: open the meaning
  // reading, whose edit action then starts editing and clears the request. With no meaning reading
  // the request is dropped, so it can never start an edit later.
  const { isFlashcardEditPending, onFlashcardEditPendingChange } = panelContext;
  const meaningIndex = screens.findIndex((screen) => screen.name === "meaning");
  useEffect(() => {
    if (!isFlashcardEditPending) {
      return;
    }
    if (meaningIndex === -1) {
      onFlashcardEditPendingChange?.(false);
    } else if (activeIndex !== meaningIndex) {
      setActiveIndex(meaningIndex);
    }
  }, [activeIndex, isFlashcardEditPending, meaningIndex, onFlashcardEditPendingChange, setActiveIndex]);

  const screenByKey = useMemo(
    () => new Map(screens.map((screen) => [screen.key, screen])),
    [screens],
  );

  const renderScene = useCallback(
    ({ route }: { route: { key: string } }) => (
      <RouteBodyStateScope name={`reading:${route.key}`}>
        {screenByKey.get(route.key)?.render() ?? null}
      </RouteBodyStateScope>
    ),
    [screenByKey],
  );

  const tabBarProps = useMemo(() => {
    const routes = screens.map((screen) => ({
      key: screen.key,
      name: screen.name,
      params: undefined,
    }));
    const navigate = (routeName: string) => {
      const nextIndex = screens.findIndex((screen) => screen.name === routeName);
      if (nextIndex !== -1) {
        setActiveIndex(nextIndex);
      }
    };
    return createMaterialTopTabBarProps({
      activeIndex,
      getOptions: (route) => ({
        title: screens.find((screen) => screen.key === route.key)?.title ?? route.name,
      }),
      navigate,
      routes,
      stateKey: `morpheme-mnemonics-${morphemeHrefObject.href}`,
      withDispatchNavigation: true,
    });
  }, [activeIndex, morphemeHrefObject, screens, setActiveIndex]);

  // Android nests this pager inside the card-panel (outer) pager. The inner
  // ViewPager2 intercepts horizontal drags natively and cancels any ancestor JS
  // pan, so the back-swipe to info can't be recognised here. Instead we
  // report whether we're on the first (meaning) tab and let the OUTER pager own
  // the rightward back-swipe there (it has info one page to the left). On
  // the first tab the inner pager has nothing to its left, so handing that drag
  // to the outer pager is safe and keeps native finger-following both ways.
  useEffect(() => {
    onFirstTabChange?.(activeIndex === 0);
  }, [activeIndex, onFirstTabChange]);

  if (readingNavigation === "browse") {
    const browseScreens = screens.map((screen, index) =>
      screen.name === "info"
        ? screen
        : {
            ...screen,
            render: () => (
              <View
                className="min-h-0 flex-1"
                style={{ paddingTop: pageTabs?.contentTopInset ?? 0 }}
              >
                <MorphemePanelsContext.Provider
                  value={{
                    ...panelContext,
                    // Neighbouring pages stay laid out for a swipe, but only the selected reading
                    // publishes its edit action into the sheet's More menu.
                    onToolbarEditActionChange:
                      index === activeIndex ? panelContext.onToolbarEditActionChange : undefined,
                  }}
                >
                  {screen.render()}
                </MorphemePanelsContext.Provider>
                {/* The band under the floating toolbar and status bar continues the summary sheet's
                    surface, so the sheet reads as coming down from the
                    top of the device. Drawn last, it also hides the sheet's shadow along that seam. */}
                {pageTabs === undefined || pageTabs.contentTopInset === 0 ? null : (
                  <View
                    className="absolute inset-x-0 top-0 bg-surface"
                    pointerEvents="none"
                    style={{ height: pageTabs.contentTopInset }}
                    testID="mnemonic-reading-top-band"
                  />
                )}
              </View>
            ),
          },
    );
    return (
      <BrowseMnemonicTabs
        activeIndex={activeIndex}
        grapheme={grapheme}
        onIndexChange={setActiveIndex}
        pagerTopExtension={pageTabs?.pagerTopExtension}
        readings={tabButtonList.map((tab) => tab.pinyin)}
        renderTabBar={pageTabs?.renderTabBar}
        screens={browseScreens}
        swipeEnabled={pageTabs?.swipeEnabled}
      />
    );
  }

  return (
    <View className={isHostedInLearnCard ? "flex-1 bg-surface" : "flex-1 bg-background"}>
      <TabView
        commonOptions={{ sceneStyle: { backgroundColor: "transparent" } }}
        initialLayout={{ width: layout.width }}
        // Each reading's scene decodes a full-width image. Mount a reading when it is first shown
        // (plus the neighbour a swipe can reveal) instead of every reading up front; a Learn card
        // cannot swipe, so it mounts only the readings the learner opens.
        lazy
        lazyPreloadDistance={isHostedInLearnCard ? 0 : 1}
        navigationState={{
          index: activeIndex,
          routes: screens.map((screen) => ({
            key: screen.key,
            title: screen.title,
          })),
        }}
        onIndexChange={setActiveIndex}
        renderScene={renderScene}
        renderTabBar={() => (
          <MorphemeMnemonicsViewTabBar {...tabBarProps} tabButtonList={tabButtonList} />
        )}
        // A Learn card switches between its glyph and pinyin sections only through the binder tab.
        swipeEnabled={!isHostedInLearnCard}
        tabBarPosition="bottom"
      />
    </View>
  );
}
